import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { dailyResults } from "@/lib/studentsApi";
import { ObservationReport } from "@/components/students/ObservationReport";

const COLS = [["date", "Date"], ["widget_views", "Widget Views"], ["subscriptions", "Subscriptions"], ["ai_continues", "AI Continues"], ["ai_connections", "AI Connections"],
  ["assessments_started", "Assessments Started"], ["assessments_completed", "Assessments Completed"], ["credits_used", "AI Credits Used"], ["cost_inr", "₹ Cost"], ["conversion_pct", "Conversion Rate"]];
const fmt = (k, v) => v == null ? "—" : k === "conversion_pct" ? `${v}%` : k === "cost_inr" ? `₹${v.toFixed ? v.toFixed(2) : v}` : k === "credits_used" ? Number(v).toFixed(2) : v;
const isToday = (d) => d === new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);

export default function DailyResults() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => { if (user?.permissions?.manage) dailyResults(days).then(setData).catch(() => toast.error("Could not load daily results")); }, [days, user]);
  if (user && !user.permissions?.manage) return <Navigate to="/" replace />;
  const t = data?.totals || {};
  return (
    <div className="space-y-6" data-testid="daily-results-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><CalendarDays className="h-6 w-6 text-primary" /> Daily Results</h1>
          <p className="text-sm text-muted-foreground mt-1">Validation period · one row per IST day · results vs actual AI credits consumed. Observe → measure → do not optimize.</p>
        </div>
        <div className="flex items-center rounded-lg border p-0.5 text-xs font-medium">
          {[15, 30, 60].map((d) => <button key={d} data-testid={`daily-range-${d}`} onClick={() => setDays(d)} className={`rounded-md px-2.5 py-1 transition-colors ${days === d ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{d}d</button>)}
        </div>
      </div>

      <ObservationReport />

      {data && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="daily-roi">
          {[["Subscriptions (cumulative)", t.subscriptions, `${t.widget_views ?? 0} widget views · ${t.conversion_pct ?? "—"}% conversion`],
            ["AI connections (cumulative)", t.ai_connections, `${t.ai_continues ?? 0} continues · ${t.assessments_completed ?? 0} assessments completed`],
            ["AI credits used (cumulative)", Number(t.credits_used || 0).toFixed(2), `₹${Number(t.cost_inr || 0).toFixed(2)} · actual consumption, not budget`],
            ["Cost per result", t.inr_per_subscription != null ? `₹${t.inr_per_subscription}` : "—", `per subscription · ${t.inr_per_ai_connection != null ? `₹${t.inr_per_ai_connection}` : "—"} per AI connection · ${t.credits_per_subscription ?? "—"} credits/sub`]].map(([l, v, s]) => (
            <div key={l} className="rounded-xl border bg-card p-4"><div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{l}</div><div className="mt-1 font-serif text-3xl font-semibold">{v ?? 0}</div><div className="mt-1 text-[11px] text-muted-foreground">{s}</div></div>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border bg-card" data-testid="daily-results-table">
        {!data ? <div className="h-48 animate-pulse" /> : (
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-left text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
              <tr>{COLS.map(([k, l]) => <th key={k} className={`px-3 py-2 ${k === "date" ? "" : "text-right"}`}>{l}</th>)}</tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.date} className={`border-t ${isToday(r.date) ? "bg-amber-50/60" : ""}`} data-testid={`daily-row-${r.date}`}>
                  {COLS.map(([k]) => <td key={k} className={`px-3 py-1.5 ${k === "date" ? "font-mono text-xs" : "text-right font-mono"}`}>{k === "date" ? `${r.date}${isToday(r.date) ? " · today" : ""}` : fmt(k, r[k])}</td>)}
                </tr>
              ))}
              <tr className="border-t-2 bg-secondary/40 font-semibold" data-testid="daily-cumulative-row">
                {COLS.map(([k]) => <td key={k} className={`px-3 py-2 ${k === "date" ? "font-mono text-xs" : "text-right font-mono"}`}>{k === "date" ? `Cumulative · ${data.days}d` : fmt(k, t[k])}</td>)}
              </tr>
            </tbody>
          </table>
        )}
      </div>
      {data && <p className="text-[11px] text-muted-foreground">{data.credit_basis}. ₹ at {data.inr_per_usd}/USD (Finance settings). Conversion = subscriptions ÷ widget views.</p>}
    </div>
  );
}
